import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  CheckCircle, 
  Clock, 
  XCircle, 
  AlertTriangle, 
  Download,
  RefreshCw,
  TrendingUp,
  Users,
  Bot,
  MessageSquare,
  Shield,
  Activity
} from 'lucide-react';
import { apiStatusService, EndpointStatus, type ApiHealthData, type ApiEndpoint, type BackendRequirements } from '@/services/api/api-status';

/**
 * Props for the API Status Dashboard component
 */
interface APIStatusDashboardProps {
  /** Optional class name for styling */
  className?: string;
}

/**
 * Get icon and styling for endpoint status
 */
const getStatusDisplay = (status: EndpointStatus) => {
  switch (status) {
    case EndpointStatus.IMPLEMENTED:
      return {
        icon: <CheckCircle className="h-4 w-4" />,
        label: '✅ Implemented',
        className: 'text-green-600 bg-green-100',
        badgeVariant: 'default' as const
      };
    case EndpointStatus.IN_PROGRESS:
      return {
        icon: <Clock className="h-4 w-4" />,
        label: '🚧 In Progress', 
        className: 'text-yellow-600 bg-yellow-100',
        badgeVariant: 'secondary' as const
      };
    case EndpointStatus.NEEDED:
      return {
        icon: <XCircle className="h-4 w-4" />,
        label: '❌ Needed',
        className: 'text-red-600 bg-red-100',
        badgeVariant: 'destructive' as const
      };
    case EndpointStatus.CHECKING:
      return {
        icon: <RefreshCw className="h-4 w-4 animate-spin" />,
        label: '🔄 Checking',
        className: 'text-blue-600 bg-blue-100',
        badgeVariant: 'outline' as const
      };
    case EndpointStatus.ERROR:
      return {
        icon: <AlertTriangle className="h-4 w-4" />,
        label: '⚠️ Error',
        className: 'text-orange-600 bg-orange-100',
        badgeVariant: 'destructive' as const
      };
    default:
      return {
        icon: <XCircle className="h-4 w-4" />,
        label: 'Unknown',
        className: 'text-gray-600 bg-gray-100',
        badgeVariant: 'outline' as const
      };
  }
};

/**
 * Get priority styling
 */
const getPriorityDisplay = (priority: string) => {
  switch (priority) {
    case 'critical':
      return { className: 'text-red-700 bg-red-50', label: 'Critical' };
    case 'high':
      return { className: 'text-orange-700 bg-orange-50', label: 'High' };
    case 'medium':
      return { className: 'text-yellow-700 bg-yellow-50', label: 'Medium' };
    case 'low':
      return { className: 'text-gray-700 bg-gray-50', label: 'Low' };
    default:
      return { className: 'text-gray-700 bg-gray-50', label: priority };
  }
};

/**
 * Format response time for display
 */
const formatResponseTime = (ms?: number): string => {
  if (!ms) return 'N/A';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

/**
 * Format date for display
 */
const formatDate = (date?: Date): string => {
  if (!date) return 'Never';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

/**
 * API Status Dashboard Component
 * 
 * Displays comprehensive API endpoint status, health metrics, and coordination tools
 * for the developer role to track backend implementation progress.
 */
export function APIStatusDashboard({ className }: APIStatusDashboardProps) {
  const [healthData, setHealthData] = useState<ApiHealthData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Load health data on mount
  useEffect(() => {
    loadHealthData();
  }, []);

  /**
   * Load API health data
   */
  const loadHealthData = async () => {
    try {
      setIsLoading(true);
      const data = await apiStatusService.getAPIHealthData();
      setHealthData(data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load API health data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refresh health data
   */
  const refreshHealthData = async () => {
    try {
      setIsRefreshing(true);
      const data = await apiStatusService.getAPIHealthData();
      setHealthData(data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to refresh API health data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  /**
   * Export backend requirements
   */
  const exportBackendRequirements = () => {
    try {
      const requirements = apiStatusService.generateBackendRequirements();
      const dataStr = JSON.stringify(requirements, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `backend-requirements-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export backend requirements:', error);
    }
  };

  /**
   * Export as CSV format
   */
  const exportAsCSV = () => {
    try {
      const endpoints = apiStatusService.getAllEndpoints();
      const headers = ['Endpoint', 'Method', 'Status', 'Priority', 'Usage', 'Response Time', 'Last Checked', 'Notes'];
      
      const csvContent = [
        headers.join(','),
        ...endpoints.map(endpoint => [
          endpoint.path,
          endpoint.method,
          endpoint.status,
          endpoint.priority,
          `"${endpoint.usage}"`,
          formatResponseTime(endpoint.responseTime),
          formatDate(endpoint.lastChecked),
          `"${endpoint.notes || ''}"`
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `api-endpoints-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export CSV:', error);
    }
  };

  // Organize endpoints by category
  const endpointsByCategory = useMemo(() => {
    if (!healthData) return {};

    const endpoints = apiStatusService.getAllEndpoints();
    
    const categories = {
      'Authentication & Authorization': endpoints.filter(ep => ep.path.includes('/auth')),
      'Client Management': endpoints.filter(ep => ep.path.includes('/clients') && !ep.path.includes('/leads') && !ep.path.includes('/conversations') && !ep.path.includes('/analytics') && !ep.path.includes('/bots')),
      'Lead Management': endpoints.filter(ep => ep.path.includes('/leads')),
      'Conversations & Analytics': endpoints.filter(ep => ep.path.includes('/conversations') || ep.path.includes('/analytics')),
      'Bot Management': endpoints.filter(ep => ep.path.includes('/bots')),
      'User Management': endpoints.filter(ep => ep.path.includes('/users')),
      'Infrastructure': endpoints.filter(ep => ep.path === '/health'),
      'Webhooks': endpoints.filter(ep => ep.path.includes('/webhooks'))
    };

    // Remove empty categories
    Object.keys(categories).forEach(key => {
      if (categories[key as keyof typeof categories].length === 0) {
        delete categories[key as keyof typeof categories];
      }
    });

    return categories;
  }, [healthData]);

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            API Status Dashboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin mr-2" />
            Loading API status...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">API Status Dashboard</h2>
          <p className="text-muted-foreground">Monitor backend API implementation progress and coordination</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={refreshHealthData} disabled={isRefreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={exportAsCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={exportBackendRequirements}>
            <Download className="h-4 w-4 mr-2" />
            Export Requirements
          </Button>
        </div>
      </div>

      {/* Health Overview Cards */}
      {healthData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Overall Status</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${
                healthData.overallStatus === 'healthy' ? 'text-green-600' :
                healthData.overallStatus === 'degraded' ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {healthData.overallStatus === 'healthy' ? '✅ Healthy' :
                 healthData.overallStatus === 'degraded' ? '⚠️ Degraded' : '🚨 Critical'}
              </div>
              <p className="text-xs text-muted-foreground">
                {healthData.blockingEndpoints.length} blocking issues
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Implementation Progress</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{healthData.implementationProgress}%</div>
              <Progress value={healthData.implementationProgress} className="mt-2" />
              <p className="text-xs text-muted-foreground mt-1">
                {healthData.statusCounts[EndpointStatus.IMPLEMENTED]} of {Object.values(healthData.statusCounts).reduce((a, b) => a + b, 0)} endpoints
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Response Time</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatResponseTime(healthData.averageResponseTime)}</div>
              <p className="text-xs text-muted-foreground">
                Average response time
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Last Check</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatDate(lastRefresh)}</div>
              <p className="text-xs text-muted-foreground">
                Health monitoring active
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Blocking Issues Alert */}
      {healthData && healthData.blockingEndpoints.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Critical Issues Detected</AlertTitle>
          <AlertDescription>
            {healthData.blockingEndpoints.length} critical endpoints are missing and blocking core functionality. 
            These require immediate backend implementation.
          </AlertDescription>
        </Alert>
      )}

      {/* Main Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
          <TabsTrigger value="missing">Missing APIs</TabsTrigger>
          <TabsTrigger value="features">Feature Flags</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Status Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Status Breakdown</CardTitle>
                <CardDescription>Endpoint implementation status distribution</CardDescription>
              </CardHeader>
              <CardContent>
                {healthData && (
                  <div className="space-y-3">
                    {Object.entries(healthData.statusCounts).map(([status, count]) => {
                      const display = getStatusDisplay(status as EndpointStatus);
                      return (
                        <div key={status} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {display.icon}
                            <span className="font-medium">{display.label}</span>
                          </div>
                          <Badge variant={display.badgeVariant}>{count}</Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Checks</CardTitle>
                <CardDescription>Recently verified endpoints</CardDescription>
              </CardHeader>
              <CardContent>
                {healthData?.recentChecks.length ? (
                  <div className="space-y-2">
                    {healthData.recentChecks.slice(0, 5).map((endpoint, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b last:border-b-0">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{endpoint.method}</Badge>
                          <span className="text-sm font-mono">{endpoint.path}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusDisplay(endpoint.status).icon}
                          <span className="text-xs text-muted-foreground">
                            {formatDate(endpoint.lastChecked)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No recent checks available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Endpoints Tab */}
        <TabsContent value="endpoints" className="space-y-4">
          {Object.entries(endpointsByCategory).map(([category, endpoints]) => (
            <Card key={category}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {category === 'Authentication & Authorization' && <Shield className="h-5 w-5" />}
                  {category === 'Client Management' && <Users className="h-5 w-5" />}
                  {category === 'Conversations & Analytics' && <MessageSquare className="h-5 w-5" />}
                  {category === 'Bot Management' && <Bot className="h-5 w-5" />}
                  {category.includes('Management') && <Users className="h-5 w-5" />}
                  {category === 'Infrastructure' && <Activity className="h-5 w-5" />}
                  {category}
                </CardTitle>
                <CardDescription>{endpoints.length} endpoints in this category</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Endpoint</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Response Time</TableHead>
                      <TableHead>Usage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {endpoints.map((endpoint, index) => {
                      const statusDisplay = getStatusDisplay(endpoint.status);
                      const priorityDisplay = getPriorityDisplay(endpoint.priority);
                      
                      return (
                        <TableRow key={index}>
                          <TableCell className="font-mono text-sm">{endpoint.path}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{endpoint.method}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {statusDisplay.icon}
                              <span className="text-sm">{statusDisplay.label}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={priorityDisplay.className}>
                              {priorityDisplay.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatResponseTime(endpoint.responseTime)}
                          </TableCell>
                          <TableCell className="text-sm max-w-xs truncate">
                            {endpoint.usage}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Missing APIs Tab */}
        <TabsContent value="missing" className="space-y-4">
          {healthData && (
            <>
              {/* Critical Missing */}
              {healthData.blockingEndpoints.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-red-600">🚨 Critical Missing Endpoints</CardTitle>
                    <CardDescription>These endpoints are blocking core functionality and need immediate implementation</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {healthData.blockingEndpoints.map((endpoint, index) => (
                        <div key={index} className="p-3 border border-red-200 rounded-lg bg-red-50">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{endpoint.method}</Badge>
                              <span className="font-mono text-sm">{endpoint.path}</span>
                            </div>
                            <Badge className="bg-red-100 text-red-700">Critical</Badge>
                          </div>
                          <p className="text-sm text-gray-700">{endpoint.usage}</p>
                          {endpoint.notes && (
                            <p className="text-xs text-red-600 mt-1">{endpoint.notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Missing by Priority */}
              {(() => {
                const missingByPriority = apiStatusService.getMissingEndpointsByPriority();
                return Object.entries(missingByPriority)
                  .filter(([priority]) => priority !== 'critical') // Already shown above
                  .map(([priority, endpoints]) => {
                    const priorityDisplay = getPriorityDisplay(priority);
                    return (
                      <Card key={priority}>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Badge className={priorityDisplay.className}>{priorityDisplay.label} Priority</Badge>
                            Missing Endpoints
                          </CardTitle>
                          <CardDescription>{endpoints.length} endpoints needed for enhanced functionality</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {endpoints.map((endpoint, index) => (
                              <div key={index} className="flex items-center justify-between py-2 border-b last:border-b-0">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline">{endpoint.method}</Badge>
                                  <span className="font-mono text-sm">{endpoint.path}</span>
                                </div>
                                <p className="text-sm text-muted-foreground max-w-xs truncate">
                                  {endpoint.usage}
                                </p>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  });
              })()}
            </>
          )}
        </TabsContent>

        {/* Feature Flags Tab */}
        <TabsContent value="features" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Feature Availability</CardTitle>
              <CardDescription>Feature flags generated from endpoint availability</CardDescription>
            </CardHeader>
            <CardContent>
              {healthData && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(healthData.featureFlags).map(([feature, enabled]) => (
                    <div key={feature} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-2">
                        {enabled ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                        <span className="font-medium">{feature}</span>
                      </div>
                      <Badge variant={enabled ? 'default' : 'destructive'}>
                        {enabled ? 'Available' : 'Disabled'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}